import React, { useState } from 'react';
import BarcodeFilled from '@uber/base-icons/barcode';
import Hide from '@uber/base-icons/hide';
import Lock from '@uber/base-icons/lock';
import LockOpen from '@uber/base-icons/lock-open';
import Pencil from '@uber/base-icons/pencil';
import Show from '@uber/base-icons/show';
import ThreeDotsHorizontal from '@uber/base-icons/three-dots-horizontal';
import TrashCan from '@uber/base-icons/trash-can';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SHAPE, SIZE as BUTTON_SIZE } from 'baseui/button';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { ANNOTATION_TYPE } from '../../../image/labelling/constants';
import { ActionButton } from '../../side-panel/annotation-details/actions/action-button';
import { ICON_SIZE } from '../../side-panel/annotation-details/constants';
import type { AnnotationInfoT } from '../../side-panel/annotations/types';
type PropsT = {
  annotation: AnnotationInfoT;
  onEditClick: () => void;
  onTrackClick?: () => void;
  onLockClick: (locked: boolean) => void;
  onVisibilityClick: () => void;
  isSelected: boolean;
  showAllAnnotations: boolean;
  onDeleteClick: () => void;
};
type ActionConfigT = {
  key: string;
  label: string;
  Icon?: React.ComponentType<{
    size?: number | string;
    color?: string;
  }>;
  onClick: (close: () => void) => void;
  disabled?: boolean;
  testId: string;
};
const buildActions = ({
  annotation,
  onEditClick,
  onTrackClick,
  onLockClick,
  onVisibilityClick,
  isSelected,
  showAllAnnotations,
  onDeleteClick
}: PropsT): ActionConfigT[] => {
  const actions: ActionConfigT[] = [{
    key: 'properties',
    label: 'Properties',
    Icon: Pencil,
    onClick: close => {
      onEditClick();
      close();
    },
    testId: 'more-actions-properties'
  }];
  if (onTrackClick) {
    actions.push({
      key: 'track',
      label: 'Track',
      Icon: BarcodeFilled,
      onClick: close => {
        onTrackClick();
        close();
      },
      testId: 'more-actions-track'
    });
  }
  const {
    isLocked
  } = annotation;
  const isFrameClassification = annotation.annotationType === ANNOTATION_TYPE.FRAME_CLASSIFICATION;
  if (!isFrameClassification) {
    actions.push({
      key: 'lock',
      label: isLocked ? 'Unlock' : 'Lock',
      Icon: isLocked ? LockOpen : Lock,
      onClick: close => {
        onLockClick(!isLocked);
        close();
      },
      testId: 'more-actions-lock'
    });
  }
  if (isSelected && !isFrameClassification) {
    actions.push({
      key: 'visibility',
      label: showAllAnnotations ? 'Hide others' : 'Show all',
      Icon: showAllAnnotations ? Hide : Show,
      onClick: close => {
        onVisibilityClick();
        close();
      },
      testId: 'more-actions-visibility'
    });
  }
  actions.push({
    key: 'delete',
    label: 'Delete',
    Icon: TrashCan,
    onClick: close => {
      onDeleteClick();
      close();
    },
    testId: 'more-actions-delete'
  });
  return actions;
};
export function MoreActionsButton({
  annotation,
  onEditClick,
  onTrackClick,
  onLockClick,
  onVisibilityClick,
  isSelected,
  showAllAnnotations,
  onDeleteClick
}: PropsT) {
  const [css, theme] = useStyletron();
  const [isOpen, setIsOpen] = useState(false);
  const actions = buildActions({
    annotation,
    onEditClick,
    onTrackClick,
    onLockClick,
    onVisibilityClick,
    isSelected,
    showAllAnnotations,
    onDeleteClick
  });
  return <StatefulPopover content={({
    close
  }) => <div className={css({
    padding: theme.sizing.scale300,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.sizing.scale300,
    maxWidth: 'max-content'
  })}>
          {actions.map(({
      key,
      label,
      Icon,
      onClick,
      disabled,
      testId
    }) => <Button key={key} kind={BUTTON_KIND.tertiary} size={BUTTON_SIZE.mini} shape={SHAPE.default} onClick={() => {
      if (disabled) {
        return;
      }
      onClick(close);
    }} disabled={disabled} data-testid={testId} startEnhancer={Icon ? () => <Icon size={ICON_SIZE} color={theme.colors.contentPrimary} /> : undefined} overrides={{
      BaseButton: {
        style: {
          justifyContent: 'flex-start',
          width: '100%',
          padding: theme.sizing.scale200
        }
      }
    }}>
              {label}
            </Button>)}
        </div>} returnFocus autoFocus placement={PLACEMENT.bottomLeft} onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)}>
      <ActionButton icon={<ThreeDotsHorizontal size={ICON_SIZE} color={theme.colors.contentPrimary} />} name="More Options" tooltipContent="More actions for this annotation" isActive={isOpen} />
    </StatefulPopover>;
}